import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Form, Icon } from "semantic-ui-react";

import { Can, InterventionModal, Recalls, useSelectedDealer, useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { RenderInterventions } from "modules/Appointments/components/NewAppointment/components/InterventionsFormSection/components";
import { canWriteBackCustomerData, generateDMSNr } from "utils/appointmentUtils";

import type { AddInterventionAndAppendPinPayloads } from "components";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";
import type { AppointmentSection } from "modules/Appointments/components/NewAppointment";
import type { Dispatch, SetStateAction } from "react";

type InterventionsFormSectionProps = {
  interventionsAndPins: AddInterventionAndAppendPinPayloads[];
  setInterventionsAndPins: Dispatch<SetStateAction<AddInterventionAndAppendPinPayloads[]>>;
};

const FormHelper = createFormHelper<AppointmentSection>();

export const InterventionsFormSection = ({ interventionsAndPins, setInterventionsAndPins }: InterventionsFormSectionProps) => {
  const { t } = useTranslation();

  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();
  const [showInterventionModal, setShowInterventionModal] = useState(false);

  const { watch } = FormHelper.useFormContext();
  const [regNr, vinNr, hasRecallsData] = watch(["car.reg_nr", "car.vin_nr", "has_recalls_data"]);

  const hasWriteBackPermissionForCustomerData = canWriteBackCustomerData(selectedLocation);
  const recallInterventions = interventionsAndPins.map(ip => ({
    ...ip.intervention,
    attachments: ip.intervention.attachments?.map(a => ({ ...a, id: 0, visible_to_customer: true, intervention_id: 0, user_id: 0, is_final_car_check: false })) || []
  }));

  const handleAddRecalls = (recallsInterventions: AddInterventionPayload[]) =>
    setInterventionsAndPins(prev => [
      ...prev,
      ...recallsInterventions.map(ri => ({
        intervention: {
          appointment_id: ri.appointment_id,
          customer_ok: true,
          visible_to_customer: true,
          description: ri.description,
          elements: ri.elements,
          price: ri.price,
          question_result_id: ri.question_result_id,
          title: ri.title,
          dms_nr: generateDMSNr(),
          attachments: ri.attachments,
          pon_recall_id: ri.pon_recall_id,
          rdw_recall_id: ri.rdw_recall_id,
          recalls_id: ri.recalls_id,
          reference_nr: ri.reference_nr
        }
      }))
    ]);

  const handleToggleInterventionModal = () => setShowInterventionModal(prev => !prev);
  const handleAddIntervention = (interventionAndPin: AddInterventionAndAppendPinPayloads) => setInterventionsAndPins(prev => [...prev, interventionAndPin]);

  return (
    <>
      <RenderInterventions interventionsAndPins={interventionsAndPins} setInterventionsAndPins={setInterventionsAndPins} />

      <Can I="create" the="interventions">
        <Form.Group className="NewAppointment__CarDetails">
          <Form.Button icon color="green" type="button" disabled={!regNr} className="mt-14 ml-0" onClick={handleToggleInterventionModal}>
            <Icon className="plus" />
            {t("v8_add_intervention", "Add Intervention")}
          </Form.Button>

          {regNr && (
            <Recalls
              dealerLocationId={selectedLocation?.id}
              showClosedRecalls={false}
              onConfirmSelection={handleAddRecalls}
              selectOnly
              vin={vinNr}
              licensePlate={regNr}
              interventions={recallInterventions}
              hasRecallsData={hasRecallsData}
            />
          )}
        </Form.Group>
      </Can>
      <InterventionModal
        appointmentId={-1}
        carId={-1}
        isCustomerOk
        showAddSubitems
        showFileUpload={false}
        location={selectedLocation ?? undefined}
        dealer={selectedDealer}
        open={showInterventionModal}
        canWriteBackCustomerData={hasWriteBackPermissionForCustomerData}
        onSubmit={handleAddIntervention}
        onClose={handleToggleInterventionModal}
      />
    </>
  );
};
